import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
} from '@radix-ui/react-icons';
import { Box, Card, Flex, Heading, IconButton, Text } from '@radix-ui/themes';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { MetricHelper } from 'classes/helpers/metric.helper';
import {
  DOT_RGB_BLUE,
  DOT_RGB_GREEN,
  DOT_RGB_YELLOW,
  PlateCanvas,
} from 'classes/plate-canvas';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import env from 'config';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { isAfter, parseISO } from 'date-fns';
import {
  BASEBALL_PNG_PX,
  DOT_SIZE_SM,
  MAX_SHOT_OPACITY,
  SHOT_OPACITY_DELTA,
} from 'enums/canvas';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import useLatest from 'hooks/useLatestRef';
import { DefaultTFuncReturn, t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { AimingHelper } from 'lib_ts/classes/aiming.helper';
import { EllipseHelper } from 'lib_ts/classes/ellipse.helper';
import {
  FT_TO_INCHES,
  IN_TO_CM,
  numberToImperial,
} from 'lib_ts/classes/math.utilities';
import { VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { MIN_ON_DEMAND_SHOTS } from 'lib_ts/enums/ellipses';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { PlateWarning } from 'lib_ts/enums/plate.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  FF_NAME_CONTINUOUS_TRAINING,
  FF_NAME_ON_DEMAND_MODE,
} from 'lib_ts/enums/training.enums';
import { ICovarianceSummary, IEllipse } from 'lib_ts/interfaces/i-ellipses';
import { DEFAULT_MACHINE_STATE } from 'lib_ts/interfaces/i-machine-state';
import { IAimingOutputsWithShots } from 'lib_ts/interfaces/i-pitch-aiming';
import {
  DEFAULT_PLATE,
  IPitch,
  IPlateLoc,
  IPlateLocExt,
} from 'lib_ts/interfaces/pitches';
import { clamp, clone, maxBy } from 'lodash';
import {
  CSSProperties,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Slider from 'react-input-slider';
import { useAuthStore } from 'stores/use-auth-store';
import { useCookiesStore } from 'stores/use-cookies-store';
import useHittersStore from 'stores/use-hitters-store';
import useMiscStore from 'stores/use-misc-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';
import { Debugger } from './debugger';
import { ToggleShots } from './toggle-shots';

const REDRAW_DEQUEUE_MS = 100;

const ENABLE_EXTRA_CLUSTER = false;
const ENABLE_REDRAW_WHILE_DRAGGING = false;

// ? draw shots for on-demand
const ENABLE_ON_DEMAND_SHOTS = true;

// don't let the user go further than +/- feet from the center
const ABS_MAX_HORIZONTAL_FT = 5.5;

interface IReleaseAxis {
  axis: 'x' | 'y';
  upIcon: JSX.Element;
  downIcon: JSX.Element;
}

const CONTROLS: IReleaseAxis[] = [
  {
    axis: 'x',
    upIcon: <ArrowRightIcon />,
    downIcon: <ArrowLeftIcon />,
  },
  {
    axis: 'y',
    upIcon: <ArrowUpIcon />,
    downIcon: <ArrowDownIcon />,
  },
];

interface IProps {
  // allows temporary suppression of new shots (e.g. sidebar is visible in the bg while training in a dialog)
  drawShots?: boolean;

  pitch?: IPitch;

  onUpdate: (plate: IPlateLoc) => void;

  border?: boolean;
  disabled?: boolean;

  showControls?: boolean;
  landscape?: boolean;

  // for safety checks and adaptive strike zone
  useHitter?: boolean;

  // optionally specify a starting plate location (e.g. when editing a pitch)
  default_plate_x?: number;
  default_plate_z?: number;

  sliderStyle?: CSSProperties;
  sizerStyle?: CSSProperties;
  controlsStyle?: CSSProperties;

  isDesigner?: boolean;

  portraitControls?: boolean;
}

interface ISummary {
  header: string;
  content: string;
}

interface IControlsProps extends IProps {
  summaries: ISummary[];
  // the units per dir is determined by the context + metric vs imperial
  adjustPosition: (axis: string, dir: 1 | -1) => void;
}

// MARK: Controls
const Controls = (props: IControlsProps) => {
  if (props.portraitControls) {
    return (
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.SM}
        position="absolute"
        top="-24px"
        right="0"
        style={props.controlsStyle}
      >
        <Flex
          gap={RADIX.FLEX.GAP.SM}
          justify="center"
          align="center"
          direction="column"
        >
          {props.summaries.map((item, i) => (
            <Card
              key={`summary-${item.header}`}
              style={{ minWidth: '100px', background: 'var(--color-surface)' }}
            >
              <Flex direction="column" align="center" gap={RADIX.FLEX.GAP.XS}>
                <Text size={RADIX.HEADING.SIZE.SM}>{t(item.header)}</Text>
                <Text
                  size={RADIX.HEADING.SIZE.SM}
                  color={RADIX.COLOR.SECONDARY}
                >
                  {item.content}
                </Text>
                {CONTROLS[i] && (
                  <Flex
                    gap={RADIX.FLEX.GAP.SM}
                    justify="center"
                    mt={RADIX.FLEX.GAP.XS}
                  >
                    <Box>
                      <IconButton
                        color={RADIX.COLOR.NEUTRAL}
                        variant="soft"
                        onClick={() =>
                          props.adjustPosition(CONTROLS[i].axis, -1)
                        }
                      >
                        {CONTROLS[i].downIcon}
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton
                        color={RADIX.COLOR.NEUTRAL}
                        variant="soft"
                        onClick={() =>
                          props.adjustPosition(CONTROLS[i].axis, 1)
                        }
                      >
                        {CONTROLS[i].upIcon}
                      </IconButton>
                    </Box>
                  </Flex>
                )}
              </Flex>
            </Card>
          ))}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      gap={RADIX.FLEX.GAP.SM}
      style={props.controlsStyle}
    >
      <Flex gap={RADIX.FLEX.GAP.SM} justify="center" align="center">
        {props.summaries.map((item) => (
          <Card key={`summary-${item.header}`} style={{ minWidth: '100px' }}>
            <Flex direction="column" align="center">
              <Heading size={RADIX.HEADING.SIZE.SM}>{t(item.header)}</Heading>

              {item.content}
            </Flex>
          </Card>
        ))}
      </Flex>
      <Flex gap={RADIX.FLEX.GAP.SM} justify="center">
        {CONTROLS.map((item, i) => (
          <Fragment key={`control-${i}`}>
            <Box>
              <IconButton
                color={RADIX.COLOR.NEUTRAL}
                variant="soft"
                onClick={() => props.adjustPosition(item.axis, -1)}
              >
                {item.downIcon}
              </IconButton>
            </Box>
            <Box>
              <IconButton
                color={RADIX.COLOR.NEUTRAL}
                variant="soft"
                onClick={() => props.adjustPosition(item.axis, 1)}
              >
                {item.upIcon}
              </IconButton>
            </Box>
          </Fragment>
        ))}
      </Flex>
    </Flex>
  );
};

// whenever the ellipse updates, draw a ring around the plate location
type EllipseAnimation = {
  startTime: number;
  duration: number;
};

// MARK: PlateView
export const PlateView = (props: IProps & IVerboseComponent) => {
  const isCTEnabled = useFeatureFlag(FF_NAME_CONTINUOUS_TRAINING);
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);

  // for identifying new shots
  const [mountDate] = useState(new Date());

  const current = useAuthStore((s) => s.current);
  const app = useCookiesStore((s) => s.app);

  const activeHitter = useHittersStore((s) => s.activeHitter);
  const { machine, activeStrikeZone } = useContext(MachineContext);
  const { isMetric, releaseFt, tracking_device } = machine;

  const { pitchBP, plateBP } = useContext(AimingContext);

  const trainingShotsDict = useTrainedMsStore((s) => s.trainingShotsDict);
  const readyToContTrain = useTrainedMsStore((s) => s.readyToContTrain);

  // lazy load shots for drawing plate locations and ellipses
  useEffect(() => {
    if (onDemand) {
      // on-demand doesn't need to draw shots
      return;
    }

    if (!props.pitch?.bs_hash) {
      // there is no bs_hash to load shots for
      return;
    }

    useTrainedMsStore.getState().lazyLoadShots(props.pitch.bs_hash);
  }, [onDemand, props.pitch?.bs_hash]);

  const shots = useMemo(() => {
    if (!onDemand && props.pitch?._mx_tms?._id) {
      // draw training shots for non-on-demand pitches
      return trainingShotsDict[props.pitch._mx_tms._id] ?? [];
    }

    return [];
  }, [props.pitch?._mx_tms?._id, onDemand, trainingShotsDict]);

  const newestShot = useMemo(() => {
    const newShots = shots.filter((s) =>
      isAfter(parseISO(s._created), mountDate)
    );

    return maxBy(newShots, (s) => s._created);
  }, [shots, mountDate]);

  const [isTesting, setIsTesting] = useState(false);
  const [bounds, setBounds] = useState(false);

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const mainCanvas = useRef<HTMLCanvasElement>(null);
  const ellipsesCanvas = useRef<HTMLCanvasElement>(null);
  const safetyCanvas = useRef<HTMLCanvasElement>(null);
  const rotatedShotsCanvas = useRef<HTMLCanvasElement>(null);
  const actualShotsCanvas = useRef<HTMLCanvasElement>(null);
  const testingShotCanvas = useRef<HTMLCanvasElement>(null);

  // not combined into image
  const animationCanvas = useRef<HTMLCanvasElement>(null);

  const animRef = useRef<EllipseAnimation | null>(null);
  const reqAnimFrameRef = useRef<number | null>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();

  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const reaimTimeout = useRef<NodeJS.Timeout>();
  const updateTimeout = useRef<NodeJS.Timeout>();
  const dragEndTimeout = useRef<NodeJS.Timeout>();

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  // MARK: canvasDef
  const canvasDef = useMemo(() => {
    // ignore the hitter-specific strike zone where irrelevant (e.g. pitch design)
    const strikezone = props.useHitter ? activeStrikeZone : VISIBLE_STRIKEZONE;

    if (props.landscape) {
      return PlateCanvas.makeLandscape(strikezone, isDarkMode);
    }

    return PlateCanvas.makePortrait(strikezone, isDarkMode);
  }, [props.useHitter, activeStrikeZone, props.landscape, isDarkMode]);

  // changes whenever hitter changes > new strike zone dimensions
  useEffect(() => {
    setLastUpdate(Date.now());
  }, [canvasDef]);

  // prevents the redraw logic while dragging
  const [dragging, setDragging] = useState(false);

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    // ignore last updates while dragging
    if (!ENABLE_REDRAW_WHILE_DRAGGING && dragging) {
      return;
    }

    updateTimeout.current = setTimeout(async () => {
      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [
        mainCanvas.current,
        ellipsesCanvas.current,
        safetyCanvas.current,
        rotatedShotsCanvas.current,
        actualShotsCanvas.current,
        testingShotCanvas.current,
      ].filter((c) => c) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      verboseDebug(props, 'updating img src');

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  const [slider, setSlider] = useState<IPlateLoc>({
    plate_x: props.default_plate_x ?? canvasDef.CONFIG.x.defaultFt,
    plate_z: props.default_plate_z ?? canvasDef.CONFIG.y.defaultFt,
  });

  const latestSlider = useLatest(slider);

  // when dragging finishes => queue up a redraw
  useEffect(() => {
    if (dragging) {
      return;
    }

    if (Math.abs(latestSlider.current.plate_x) > ABS_MAX_HORIZONTAL_FT) {
      // check whether the user let go while outside of valid bounds
      // note: this is different from the canvas limits, which could be wider than what we want to allow (for aspect ratio reasons)
      const safeX = clamp(
        latestSlider.current.plate_x,
        -ABS_MAX_HORIZONTAL_FT,
        ABS_MAX_HORIZONTAL_FT
      );

      const nextSlider: IPlateLoc = {
        plate_x: safeX,
        plate_z: latestSlider.current.plate_z,
      };

      setSlider(nextSlider);
      props.onUpdate(nextSlider);
    }

    setLastUpdate(Date.now());
  }, [dragging]);

  const [aimed, setAimed] = useState<IAimingOutputsWithShots>();

  // updates slider to match pitchBP and plateBP, also updates covariance and ellipses
  useEffect(() => {
    // no need to aim when just using for design purposes
    if (props.isDesigner) {
      return;
    }

    // use a timeout to prevent spamming this logic while dragging
    reaimTimeout.current = setTimeout(async () => {
      if (!pitchBP) {
        return;
      }

      // training shots need to be loaded for ellipses and plates to be drawn
      const aimingShots = await useTrainedMsStore
        .getState()
        .lazyLoadShots(pitchBP.bs_hash);

      // in the case where a pitch is untrained, there will not be any mixin TMS value
      const safeMs = pitchBP._mx_tms?.ms ?? DEFAULT_MACHINE_STATE;

      const result = AimingHelper.aimWithShots({
        priority: pitchBP.priority ?? BuildPriority.Spins,
        bs: pitchBP.bs,
        traj: pitchBP.traj,
        ms: safeMs,
        seams: pitchBP.seams,
        breaks: pitchBP.breaks,
        stepSize: undefined,
        releaseFt: machine.releaseFt,
        targetPlate: pitchBP.debugMs
          ? DEFAULT_PLATE // debug pitches should always point at the default plate instead of their saved plate location
          : plateBP,
        clampTilt: false,
        checkConfidence: false, // use all shots b/c plate view renders the result of training
        training: false,
        aimingShots: aimingShots,
      });

      setAimed(result);

      if (!result) {
        // update slider to match the plateBP (e.g. untrained pitch's saved plate location)
        verboseDebug(props, 'updating slider w/o aimWithShots');
        setSlider(plateBP);

        // clear everything if there is no aimed pitch
        setPreviousEllipse(undefined);
        setCovariance(undefined);
        return;
      }

      // update slider to match the aimed result
      verboseDebug(props, 'updating slider w/ aimWithShots', result);
      setSlider(TrajHelper.getPlateLoc(result.traj));

      const output = EllipseHelper.getCovSummaryFromShots({
        ms: result.ms,
        shots: result.usingShots,
        releaseFt: releaseFt,
      });

      setPreviousEllipse(covariance?.ellipse); // backup before changing
      setCovariance(output);

      // * DO NOT trigger props.onUpdate to avoid an infinite loop
    }, 100);

    return () => {
      clearTimeout(reaimTimeout.current);
    };
  }, [pitchBP, plateBP, machine.releaseFt, props.isDesigner]);

  const [testSlider, setTestSlider] = useState<IPlateLoc>({
    plate_x: 0,
    plate_z: 0,
  });

  // Draw Main
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = mainCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        await canvasDef.drawGround(ctx);
        canvasDef.drawStrikeZone(ctx);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [canvasDef]);

  const [covariance, setCovariance] = useState<ICovarianceSummary>();
  const latestEllipse = useLatest(covariance?.ellipse);

  // todo: usePrevious for this if it's the same thing
  const [prevEllipse, setPreviousEllipse] = useState<IEllipse>();

  // Draw Ellipses
  useEffect(() => {
    try {
      const canvas = ellipsesCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (props.disabled) {
        return;
      }

      if (onDemand) {
        return;
      }

      if (!current.plate_show_ellipses) {
        return;
      }

      if (!covariance) {
        // nothing to draw
        return;
      }

      const { ellipse } = covariance;

      if (!ellipse) {
        return;
      }

      /** don't print the multi-color ellipses in production */
      const isDebugging = env.enable.toggle_plate_debug && app.plate_show_debug;

      const ellipses: {
        color: string;
        ellipse: IEllipse;
        fixFn: (n: number) => number;
      }[] = [];

      const color = canvasDef.CONFIG.isDark
        ? // light on dark bg
          '255, 255, 255'
        : // dark on light bg
          '150, 150, 150';

      if (env.production || !isDebugging) {
        /** either prod OR not debugging */
        ellipses.push({
          /** white */
          color: color,
          ellipse: ellipse,
          fixFn: EllipseHelper.flipQ1AndQ4,
        });
      } else {
        /** must be non-prod AND debugging */
        ellipses.push({
          /** red */
          color: '255, 0, 0',
          ellipse: ellipse,
          fixFn: EllipseHelper.flipQ1AndQ4,
        });

        if (isTesting) {
          ellipses.push({
            /** light blue - unrotated ellipse */
            color: '49, 210, 242',
            ellipse: ellipse,
            fixFn: () => 0,
          });
        }
      }

      for (const ell of ellipses) {
        for (const scaling of EllipseHelper.NON_EMPTY_SCALINGS) {
          canvasDef.drawEllipse(ctx, {
            plate: slider,
            fillStyle: `rgba(${ell.color},${scaling.opacity})`,
            ellipse: ell.ellipse,
            scaling_factor: scaling.factor,
            angle_radians: ell.fixFn(ell.ellipse.angle_radians),
            bounds: bounds,
            drawBorder: scaling.drawBorder,
          });
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [
    props.disabled, // the entire widget is disabled
    current.plate_show_ellipses, // the user wants to see ellipses for regular mode
    onDemand, // the user is using on-demand mode
    slider, // ensures the ellipse is always centered on the selected location
    covariance, // * should auto-update when new shots are fired w/ on-demand
  ]);

  const canDrawShots = props.drawShots && shots.length > 0;
  const isDrawActual = canDrawShots && app.plate_show_actual;
  const isDrawRotated = canDrawShots && app.plate_show_rotated;
  const isDrawAny = isDrawActual || isDrawRotated;

  // Draw Rotated Shots
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = rotatedShotsCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!isDrawRotated) {
          return;
        }

        if (onDemand && !ENABLE_ON_DEMAND_SHOTS) {
          return;
        }

        if (!aimed) {
          return;
        }

        // excludes new shot
        const plates = EllipseHelper.getRotatedLocations({
          ms: aimed.ms,
          shots: aimed.usingShots,
          releaseFt: releaseFt,
        }).filter((loc) => loc._id !== newestShot?._id);

        verboseDebug(props, `drawing ${plates.length} old shots`, ...plates);

        for (const plate of plates) {
          const opacity = Math.max(
            0,
            MAX_SHOT_OPACITY -
              plates.findIndex((m) => m === plate) * SHOT_OPACITY_DELTA
          );

          await canvasDef.drawDot(ctx, {
            plate: plate,
            color: `rgba(${DOT_RGB_GREEN}, ${opacity})`,
            size: DOT_SIZE_SM,
            labelText: isTesting ? plate._created : undefined,
          });
        }

        if (readyToContTrain && newestShot) {
          const newPlate: IPlateLocExt = {
            ...TrajHelper.getPlateLoc(newestShot.user_traj ?? newestShot.traj),
            _created: newestShot._created,
            _id: newestShot._id,
          };

          verboseDebug(props, 'drawing new shot', newPlate);

          await canvasDef.drawDot(ctx, {
            plate: newPlate,
            color: `rgba(${DOT_RGB_YELLOW}, ${MAX_SHOT_OPACITY})`,
            size: DOT_SIZE_SM,
            labelText: isTesting ? newPlate._created : undefined,
          });
        }
      } catch (e) {
        console.error(`Failed to draw rotated shots`, e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [
    isDrawRotated,
    releaseFt,
    isCTEnabled,
    tracking_device,
    onDemand,
    aimed,
    newestShot,
    isTesting,
  ]);

  // Draw Actual Shots
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = actualShotsCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!isDrawActual) {
          return;
        }

        if (onDemand && !ENABLE_ON_DEMAND_SHOTS) {
          return;
        }

        const plates = shots.map((shot) => {
          const traj = shot.user_traj ?? shot.traj;

          const transTraj = TrajHelper.translateTraj(traj, {
            px: traj.px,
            pz: traj.pz,
            py: releaseFt,
          });

          const o: IPlateLocExt = {
            ...TrajHelper.getPlateLoc(transTraj),
            _created: shot._created,
            _id: shot._id,
          };

          return o;
        });

        verboseDebug(props, `drawing ${plates.length} actual shots`, plates);

        for (const plate of plates) {
          const opacity = Math.max(
            0,
            MAX_SHOT_OPACITY -
              plates.findIndex((m) => m === plate) * SHOT_OPACITY_DELTA
          );

          await canvasDef.drawDot(ctx, {
            plate: plate,
            color: `rgba(${DOT_RGB_YELLOW}, ${opacity})`,
            size: DOT_SIZE_SM,
            labelText: isTesting ? plate._created : undefined,
          });
        }
      } catch (e) {
        console.error(`Failed to draw actual shots`, e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [isDrawActual, onDemand, shots, releaseFt, isTesting]);

  // Draw Testing Dots
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = testingShotCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!isTesting) {
          return;
        }

        await canvasDef.drawDot(ctx, {
          plate: testSlider,
          color: `rgba(${DOT_RGB_BLUE},1)`,
          size: DOT_SIZE_SM,
          labelText: 'Test',
        });

        const currEllipse = covariance?.ellipse;

        if (currEllipse) {
          /** draw location of un-rotated test dot */
          const plate = EllipseHelper.rotatePoint({
            origin: clone(slider),
            point: clone(testSlider),
            angle_radians: -currEllipse.angle_radians,
          });

          await canvasDef.drawDot(ctx, {
            plate: plate,
            color: `rgba(${DOT_RGB_BLUE},1)`,
            size: DOT_SIZE_SM,
            labelText: 'Test (Unrotated)',
          });
        }
      } catch (e) {
        console.error(`Failed to draw test dots`, e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [isTesting, testSlider, covariance, slider]);

  // Draw Safety Region
  useEffect(() => {
    try {
      const canvas = safetyCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (props.disabled) {
        return;
      }

      if (!props.useHitter) {
        return;
      }

      if (!activeHitter) {
        return;
      }

      // todo: check if this is (now) drawing on the wrong side for some reason
      verboseDebug(props, `drawing safety region for ${activeHitter.name}`);
      canvasDef.drawStrikeZoneSafetyRegion(ctx, activeHitter);
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [props.disabled, props.useHitter, activeHitter]);

  const [warningMsg, setWarningMsg] = useState<DefaultTFuncReturn>();

  const safetyCheck = useCallback(() => {
    const ellipse = latestEllipse.current;

    if (!ellipse) {
      // we cannot get a safe location without an ellipse
      return;
    }

    const safeLoc = EllipseHelper.getSafeOverallLoc({
      input: latestSlider.current,
      ellipseWarnings: app.plate_show_ellipse_warnings,
      ellipse: ellipse,
      hitter: props.useHitter ? activeHitter : undefined,
    });

    if (!safeLoc) {
      // there was an error, see console for details
      return;
    }

    switch (safeLoc.warning) {
      case PlateWarning.DangerousHorizontal: {
        setWarningMsg(t('common.plate-warning-dangerous-horizontal'));
        break;
      }

      case PlateWarning.EllipseBoth: {
        setWarningMsg(t('common.plate-warning-ellipse-both'));
        break;
      }

      case PlateWarning.EllipseLeft: {
        setWarningMsg(t('common.plate-warning-ellipse-left'));
        break;
      }

      case PlateWarning.EllipseRight: {
        setWarningMsg(t('common.plate-warning-ellipse-right'));
        break;
      }

      default: {
        setWarningMsg(undefined);
        break;
      }
    }

    // force the aim to be further from the side w/ the batter
    if (
      env.enable.toggle_plate_safety_controls &&
      app.plate_limit_by_ellipse &&
      safeLoc.location
    ) {
      // * currently shouldn't trigger because ellipse helper does not make recommendations
      verboseDebug(props, 'safety check > slider adjustment required', {
        current: latestSlider.current,
        safe: safeLoc.location,
      });

      setSlider(safeLoc.location);
      props.onUpdate(safeLoc.location);
    }
  }, [
    app.plate_show_ellipse_warnings,
    app.plate_limit_by_ellipse,
    props.useHitter,
    activeHitter,
    props.onUpdate,
  ]);

  useEffect(() => {
    safetyCheck();
  }, [covariance, safetyCheck]);

  // draw an animated ring from the slider position
  useEffect(() => {
    if (!onDemand) {
      // animation only happens in on-demand mode
      return;
    }

    const canvas = animationCanvas.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const render = (time: number) => {
      verboseDebug(props, `animation render @ ${time}`);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const anim = animRef.current;

      // start the animation if there isn't any in progress
      if (!anim) {
        reqAnimFrameRef.current = requestAnimationFrame(render);
        return;
      }

      const elapsed = time - anim.startTime;
      const progress = elapsed / anim.duration;

      // clear the animation when done
      if (progress >= 1) {
        animRef.current = null;
        reqAnimFrameRef.current = requestAnimationFrame(render);
        return;
      }

      // render each frame of the animation while in progress
      const eased = 1 - Math.pow(1 - progress, 3);

      // prevent negative radius from breaking things
      const radius = Math.max(0, 20 + eased * 80);
      const opacity = 1 - progress;

      ctx.beginPath();

      const cx = canvasDef.ftToPxX(latestSlider.current.plate_x);
      const cy = canvasDef.ftToPxY(latestSlider.current.plate_z);

      const sampleSize = latestEllipse.current?.sampleSize ?? 0;

      const neutralColor = canvasDef.CONFIG.isDark
        ? // light on dark bg
          '255, 255, 255'
        : // dark on light bg
          '150, 150, 150';

      const color =
        sampleSize < MIN_ON_DEMAND_SHOTS
          ? // low confidence > render as neutral color
            neutralColor
          : // sufficient confidence > render as accent color
            '63, 97, 222';

      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${color}, ${opacity})`;
      ctx.lineWidth = 8;
      ctx.stroke();

      reqAnimFrameRef.current = requestAnimationFrame(render);
    };

    reqAnimFrameRef.current = requestAnimationFrame(render);

    return () => {
      if (reqAnimFrameRef.current) {
        cancelAnimationFrame(reqAnimFrameRef.current);
      }
    };
  }, [onDemand]);

  // while using on-demand, when covariance changes (e.g. updated), animate the ring
  useEffect(() => {
    if (!onDemand) {
      // animation only happens in on-demand mode
      return;
    }

    if (!covariance) {
      return;
    }

    // gather multiple changes to trigger animation only 1x
    const timeout = setTimeout(() => {
      verboseDebug(props, 'triggered animation');

      animRef.current = {
        startTime: performance.now(),
        duration: 800, // ms
      };
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [onDemand, covariance]);

  const sliderContent = (
    <div
      className="slider"
      data-testid="PlateView"
      style={{
        ...props.sliderStyle,
        aspectRatio: canvasDef.CONFIG.canvas.aspectRatio,
      }}
    >
      <div
        className="sizer"
        style={
          props.sizerStyle
            ? {
                ...props.sizerStyle,
                aspectRatio: canvasDef.CONFIG.canvas.aspectRatio,
              }
            : {
                aspectRatio: canvasDef.CONFIG.canvas.aspectRatio,
                width: '100%',
              }
        }
      >
        {/* for log rocket recordings to show the results */}
        {imgSrc && <img className="image-wrapper" src={imgSrc} />}
        <canvas
          ref={mergeCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={mainCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={ellipsesCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={safetyCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={testingShotCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={rotatedShotsCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={actualShotsCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
        />
        <canvas
          ref={animationCanvas}
          width={canvasDef.CONFIG.canvas.widthPx}
          height={canvasDef.CONFIG.canvas.heightPx}
          className="visible"
        />
        <div
          className={StringHelper.classNames([
            'slider-wrapper',
            isDrawAny ? 'animate-fade' : '',
          ])}
        >
          <Slider
            data-testid="PlateViewSlider"
            data-xcord={slider.plate_x}
            data-ycord={slider.plate_z}
            axis="xy"
            xstep={canvasDef.CONFIG.x.step}
            xmin={canvasDef.CONFIG.x.minFt}
            xmax={canvasDef.CONFIG.x.maxFt}
            x={slider.plate_x}
            ystep={canvasDef.CONFIG.y.step}
            ymin={canvasDef.CONFIG.y.minFt}
            ymax={canvasDef.CONFIG.y.maxFt}
            y={slider.plate_z}
            disabled={props.disabled}
            onChange={(values) => {
              if (isTesting) {
                setTestSlider({
                  plate_x: values.x,
                  plate_z: values.y,
                });
                return;
              }

              const nextSlider: IPlateLoc = {
                plate_x: values.x,
                plate_z: values.y,
              };

              setSlider(nextSlider);
              props.onUpdate(nextSlider);
            }}
            onDragStart={() => setDragging(true)}
            onDragEnd={() => {
              setDragging(false);

              clearTimeout(dragEndTimeout.current);

              // allow setSlider from onChange to settle the final value of slider
              dragEndTimeout.current = setTimeout(() => {
                // intention: trigger 1x after drag ends, but also after slider has updated (from onChange)
                safetyCheck();
              }, 100);
            }}
            styles={{
              track: {
                backgroundColor: 'rgba(0, 0, 255, 0)',
                width: '100%',
                height: '100%',
              },
              thumb: {
                backgroundColor: 'rgba(0, 255, 0, 0)',
                backgroundImage: 'url(/img/baseball.png)',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                borderRadius: '50%',
                boxShadow: '0 0 0 1px var(--gray-a6)',
                height: BASEBALL_PNG_PX,
                width: BASEBALL_PNG_PX,
                margin: -BASEBALL_PNG_PX / 2,
                // opacity: props.disabled ? 0 : 1,
              },
            }}
            yreverse
          />
        </div>
      </div>
    </div>
  );

  const summaries = useMemo<
    {
      header: string;
      content: string;
    }[]
  >(
    () => [
      {
        header: 'common.side',
        content: isMetric
          ? `${(MetricHelper.ftToM(slider.plate_x) * 100).toFixed(
              0
            )} ${MetricHelper.getPositionUnit(true)}`
          : numberToImperial(slider.plate_x).display,
      },
      {
        header: 'common.height',
        content: isMetric
          ? `${(MetricHelper.ftToM(slider.plate_z) * 100).toFixed(
              0
            )} ${MetricHelper.getPositionUnit(true)}`
          : numberToImperial(slider.plate_z).display,
      },
    ],
    [slider, isMetric]
  );

  /**
   *
   * @param axis 'x' or 'y'
   * @param dir direction of the change; the actual amount to change by is determined by metric vs imperial (1 cm vs 1 inch)
   */
  const adjustPosition = (axis: string, dir: 1 | -1) => {
    /** component is updating sliders, trigger parent's onUpdate with end results */
    const callback = (x: number, y: number) =>
      props.onUpdate({ plate_x: x, plate_z: y });

    const deltaIn = isMetric
      ? // cm > inches > ft
        dir / IN_TO_CM
      : // inches > ft
        dir;
    const deltaFt = deltaIn / FT_TO_INCHES;

    switch (axis) {
      case 'x': {
        const nextX = clamp(
          latestSlider.current.plate_x + deltaFt,
          canvasDef.CONFIG.x.minFt,
          canvasDef.CONFIG.x.maxFt
        );

        setSlider((prev) => ({
          ...prev,
          plate_x: nextX,
        }));

        callback(nextX, slider.plate_z);
        return;
      }

      case 'y': {
        const nextZ = clamp(
          latestSlider.current.plate_z + deltaFt,
          canvasDef.CONFIG.y.minFt,
          canvasDef.CONFIG.y.maxFt
        );

        setSlider((prev) => ({
          ...prev,
          plate_z: nextZ,
        }));

        callback(slider.plate_x, nextZ);
        return;
      }

      default: {
        return;
      }
    }
  };

  return (
    <ErrorBoundary identifier="PlateView">
      <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
        {props.border ? (
          <Card
            style={{
              padding: 0,
              background: 'var(--color-background)',
            }}
          >
            {sliderContent}
          </Card>
        ) : (
          sliderContent
        )}

        {props.showControls && (
          <Controls
            {...props}
            summaries={summaries}
            adjustPosition={adjustPosition}
          />
        )}

        {warningMsg && (
          <CommonCallout
            color={
              app.plate_limit_by_ellipse
                ? RADIX.COLOR.INFO
                : RADIX.COLOR.WARNING
            }
            text_md={
              app.plate_limit_by_ellipse
                ? [warningMsg, t('common.plate-adjustment-msg')].join('\n\n')
                : warningMsg
            }
          />
        )}

        {ENABLE_EXTRA_CLUSTER &&
          env.enable.toggle_plate_shots &&
          props.pitch && (
            <ToggleShots
              bounds={bounds}
              setBounds={setBounds}
              testing={isTesting}
              setTesting={setIsTesting}
            />
          )}

        {ENABLE_EXTRA_CLUSTER &&
          env.enable.toggle_plate_debug &&
          app.plate_show_debug && (
            <Debugger
              pitch={props.pitch}
              newShot={newestShot}
              testing={isTesting}
              slider={slider}
              testSlider={testSlider}
              currEllipse={covariance?.ellipse}
              prevEllipse={prevEllipse}
              covSummary={covariance}
            />
          )}
      </Flex>
    </ErrorBoundary>
  );
};
